import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Box,
  Button,
  Grid,
  MenuItem,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import FileDropZone from "components/FileDropZone";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getDisplayConfigurationsThunk,
  updateDisplayConfigurationsThunk,
} from "store/reducers/machineConfig";
import * as yup from "yup";

const validation = yup.object({
  display_level: yup.string("Category").required("Field required"),
  require_user_info: yup
    .boolean("Capture user details")
    .required("Field required"),
  product_display: yup.string("Product display").required("Field required"),
  logo: yup.mixed("Set logo").nullable(),
  company_name: yup.string("Company name").nullable(),
  front_idle_screen: yup
    .boolean("Front idle screen")
    .required("Field required"),
  cart_max_count: yup.number().required(),
});

const DisplayConfigInWizard = ({
  currentMachine,
  handleSkip,
  handleNext,
  setDisplayConfigCompleted,
  fullGrid = false,
}) => {
  const isFullWidth = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const dispatch = useDispatch();
  const [files, setFiles] = useState([]);
  const machineConfigState = useSelector((state) => state.machineConfig);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      display_level:
        machineConfigState.displayConfigs[currentMachine.id]?.display_level ??
        "cat",
      require_user_info:
        machineConfigState.displayConfigs[currentMachine.id]
          ?.require_user_info ?? true,
      product_display:
        machineConfigState.displayConfigs[currentMachine.id]?.product_display ??
        "grd",
      logo: machineConfigState.displayConfigs[currentMachine.id]?.logo ?? null,
      company_name:
        machineConfigState.displayConfigs[currentMachine.id]?.company_name ??
        "",
      front_idle_screen: true,
      cart_max_count:
        machineConfigState.displayConfigs[currentMachine.id]?.cart_max_count ??
        4,
      enable_refund:
        machineConfigState.displayConfigs[currentMachine.id]?.enable_refund ??
        true,
    },
    validationSchema: validation,
    onSubmit: (data) => {
      console.log(
        machineConfigState.displayConfigs[currentMachine.id]?.id,
        data
      );
      dispatch(
        updateDisplayConfigurationsThunk({
          id: machineConfigState.displayConfigs[currentMachine.id]?.id,
          data: data,
        })
      );
    },
  });
  useEffect(() => {
    formik.setFieldValue("logo", files[0] ?? null);
  }, [files]);

  useEffect(() => {
    if (machineConfigState.updateDisplayConfigurationsSuccess) {
      if (setDisplayConfigCompleted) {
        setDisplayConfigCompleted(true);
      }
      if (handleNext) {
        handleNext();
      }
    }
  }, [
    handleNext,
    machineConfigState.updateDisplayConfigurationsSuccess,
    setDisplayConfigCompleted,
  ]);

  useEffect(() => {
    dispatch(getDisplayConfigurationsThunk(currentMachine.id));
  }, [currentMachine.id, dispatch]);

  return (
    <Box sx={{ mb: 2 }}>
      <form onSubmit={formik.handleSubmit}>
        <Grid container columnSpacing={2} rowSpacing={0}>
          <Grid item xs={12} md={fullGrid ? 12 : 6} lg={fullGrid ? 12 : 4}>
            <Box display="flex" flexDirection="column">
              <Grid container columnSpacing={2} rowSpacing={0}>
                <Grid item xs={12} md={6} lg={4}>
                  <TextField
                    label="Display level"
                    variant="outlined"
                    name="display_level"
                    select
                    fullWidth
                    sx={{ mt: 3 }}
                    value={formik.values.display_level}
                    onChange={formik.handleChange("display_level")}
                    error={Boolean(formik.errors.display_level)}
                    helperText={formik.errors.display_level}
                    autoComplete="new-password"
                    required
                  >
                    <MenuItem value="cat">Category</MenuItem>
                    <MenuItem value="sub">Subcategory</MenuItem>
                    <MenuItem value="prd">Product</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6} lg={4}>
                  <TextField
                    label="Require user info"
                    variant="outlined"
                    name="require_user_info"
                    select
                    fullWidth
                    sx={{ mt: 3 }}
                    value={formik.values.require_user_info}
                    onChange={formik.handleChange("require_user_info")}
                    error={Boolean(formik.errors.require_user_info)}
                    helperText={formik.errors.require_user_info}
                    autoComplete="new-password"
                    required
                  >
                    <MenuItem value={true}>Yes</MenuItem>
                    <MenuItem value={false}>No</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6} lg={4}>
                  <TextField
                    label="Product display"
                    variant="outlined"
                    name="product_display"
                    select
                    fullWidth
                    sx={{ mt: 3 }}
                    value={formik.values.product_display}
                    onChange={formik.handleChange("product_display")}
                    error={Boolean(formik.errors.product_display)}
                    helperText={formik.errors.product_display}
                    autoComplete="new-password"
                    required
                  >
                    <MenuItem value="grd">Grid</MenuItem>
                    <MenuItem value="lin">Linear</MenuItem>
                  </TextField>
                </Grid>
              </Grid>

              <Typography variant="h6" color="gray" marginY={2}>
                Choose a logo
              </Typography>
              <FileDropZone
                maxFiles={1}
                accept={{
                  "image/png": [".png"],
                  "image/jpeg": [".jpg", ".jpeg"],
                }}
                mode="image"
                files={files}
                setFiles={setFiles}
              />
              <TextField
                label="Company name"
                variant="outlined"
                name="company_name"
                fullWidth
                sx={{ mt: 3 }}
                value={formik.values.company_name}
                onChange={formik.handleChange}
                error={Boolean(formik.errors.company_name)}
                helperText={formik.errors.company_name}
                autoComplete="new-password"
              />
              <Grid container columnSpacing={2} rowSpacing={0}>
                <Grid item xs={12} md={6} lg={4}>
                  <TextField
                    label="Front idle screen"
                    variant="outlined"
                    name="front_idle_screen"
                    select
                    fullWidth
                    sx={{ mt: 3 }}
                    value={formik.values.front_idle_screen}
                    onChange={formik.handleChange}
                    error={Boolean(formik.errors.front_idle_screen)}
                    helperText={formik.errors.front_idle_screen}
                    autoComplete="new-password"
                  >
                    <MenuItem value={true}>Yes</MenuItem>
                    <MenuItem value={false}>No</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6} lg={4}>
                  <TextField
                    label="Cart max"
                    variant="outlined"
                    name="cart_max_count"
                    fullWidth
                    sx={{ mt: 3 }}
                    value={formik.values.cart_max_count}
                    onChange={formik.handleChange}
                    error={Boolean(formik.errors.cart_max_count)}
                    helperText={formik.errors.cart_max_count}
                    autoComplete="new-password"
                  />
                </Grid>
                <Grid item xs={12} md={6} lg={4}>
                  <TextField
                    label="Enable refund"
                    variant="outlined"
                    name="enable_refund"
                    select
                    fullWidth
                    sx={{ mt: 3 }}
                    value={formik.values.enable_refund}
                    onChange={formik.handleChange}
                    error={Boolean(formik.errors.enable_refund)}
                    helperText={formik.errors.enable_refund}
                    autoComplete="new-password"
                  >
                    <MenuItem value={true}>Yes</MenuItem>
                    <MenuItem value={false}>No</MenuItem>
                  </TextField>
                </Grid>
              </Grid>
            </Box>
            {machineConfigState.updateDisplayConfigurationsError && (
              <Alert sx={{ mt: 3 }} severity="error">
                Error occurred
              </Alert>
            )}
            {machineConfigState.getDisplayConfigurationsMessage && (
              <Alert sx={{ mt: 3 }} severity="error">
                {machineConfigState.getDisplayConfigurationsMessage}
              </Alert>
            )}
            {machineConfigState.updateDisplayConfigurationsSuccess && (
              <Alert sx={{ mt: 3 }} severity="success">
                Success
              </Alert>
            )}
            <Box
              sx={{
                display: "flex",
                flexDirection: "row-reverse",
                alignItems: "center",
                mt: 3,
              }}
            >
              <LoadingButton
                loading={machineConfigState.updateDisplayConfigurationsLoading}
                variant="contained"
                fullWidth={isFullWidth}
                type="submit"
              >
                {handleSkip ? "Continue" : "Submit"}
              </LoadingButton>
              {handleSkip && (
                <Button color="inherit" onClick={handleSkip} sx={{ mr: 1 }}>
                  Skip
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default DisplayConfigInWizard;
